/**
 * Native (APNs / FCM) push registration for the HIBANE Desk shell.
 *
 * Web behaviour is untouched: every export here no-ops unless
 * `isNativeApp()` is true, and `@capacitor/push-notifications` is only
 * imported dynamically inside the native guard.
 */
import { supabase } from "@/integrations/supabase/client";
import { isNativeApp, getPlatform } from "./platform";
import type { PushPortal } from "@/lib/push/types";

export const NATIVE_PUSH_PORTAL: PushPortal = "admin";

type PermissionState = "granted" | "denied" | "prompt" | "unsupported";

let listenersBound = false;
let deepLinkHandler: ((url: string) => void) | null = null;

/** Registers the callback used when the user taps a native notification. */
export function setNativePushDeepLinkHandler(fn: ((url: string) => void) | null) {
  deepLinkHandler = fn;
}

async function loadPlugin() {
  if (!isNativeApp()) return null;
  try {
    const mod = await import("@capacitor/push-notifications");
    return mod.PushNotifications;
  } catch (err) {
    console.error("[native-push] plugin unavailable", err);
    return null;
  }
}

export async function getNativePushPermission(): Promise<PermissionState> {
  const plugin = await loadPlugin();
  if (!plugin) return "unsupported";
  try {
    const res = await plugin.checkPermissions();
    return (res.receive as PermissionState) ?? "prompt";
  } catch {
    return "unsupported";
  }
}

/** Persists the device token as a `channel = 'native'` push subscription row. */
async function saveToken(token: string): Promise<boolean> {
  const platform = getPlatform();
  if (platform === "web") return false;

  const { data: session } = await supabase.auth.getSession();
  if (!session.session) {
    console.warn("[native-push] no session yet — token save deferred");
    return false;
  }

  const { data, error } = await supabase.functions.invoke("register-push-subscription", {
    body: {
      portal: NATIVE_PUSH_PORTAL,
      channel: "native",
      native_token: token,
      native_platform: platform,
      platform,
      device_label: `HIBANE Desk (${platform === "ios" ? "iPhone/iPad" : "Android"})`,
      user_agent: navigator.userAgent,
    },
  });

  if (error || (data && data.ok === false)) {
    console.error("[native-push] token registration failed", error ?? data?.error);
    return false;
  }
  console.log("[native-push] device token registered");
  return true;
}

function extractDeepLink(data: Record<string, unknown> | undefined): string | null {
  if (!data) return null;
  const raw = (data.deep_link ?? data.deepLink ?? data.link) as string | undefined;
  if (!raw || typeof raw !== "string") return null;
  return raw;
}

/**
 * Requests permission (once), registers with APNs/FCM, stores the token and
 * wires tap handling. Safe to call repeatedly — listeners bind only once.
 */
export async function initNativePush(): Promise<{ enabled: boolean; reason?: string }> {
  const plugin = await loadPlugin();
  if (!plugin) return { enabled: false, reason: "unsupported" };

  if (!listenersBound) {
    listenersBound = true;

    await plugin.addListener("registration", (t: { value: string }) => {
      void saveToken(t.value);
    });

    await plugin.addListener("registrationError", (err: unknown) => {
      console.error("[native-push] registration error", err);
    });

    // Foreground delivery — the OS does not show a banner, so let the app's
    // existing bell/badge realtime subscriptions handle it. Nothing to do here
    // beyond logging; we never surface raw payloads to the user.
    await plugin.addListener("pushNotificationReceived", () => {
      console.log("[native-push] foreground notification received");
    });

    // Tap → open the Desk deep link the dispatcher resolved.
    await plugin.addListener(
      "pushNotificationActionPerformed",
      (action: { notification?: { data?: Record<string, unknown> } }) => {
        const url = extractDeepLink(action?.notification?.data) ?? "/admin";
        if (deepLinkHandler) deepLinkHandler(url);
        else window.location.assign(url);
      },
    );
  }

  try {
    let perm = await plugin.checkPermissions();
    if (perm.receive === "prompt" || perm.receive === "prompt-with-rationale") {
      perm = await plugin.requestPermissions();
    }
    if (perm.receive !== "granted") {
      return { enabled: false, reason: perm.receive === "denied" ? "blocked" : "permission_denied" };
    }
    await plugin.register();
    return { enabled: true };
  } catch (err) {
    console.error("[native-push] init failed", err);
    return { enabled: false, reason: "generic" };
  }
}

/** Revokes this device's native subscription rows for the Desk portal. */
export async function disableNativePush(): Promise<boolean> {
  const plugin = await loadPlugin();
  if (!plugin) return false;
  try {
    const { data: userData } = await supabase.auth.getUser();
    if (userData.user) {
      await supabase
        .from("push_subscriptions")
        .update({ revoked_at: new Date().toISOString() })
        .eq("user_id", userData.user.id)
        .eq("channel", "native")
        .eq("native_platform", getPlatform())
        .is("revoked_at", null);
    }
    await plugin.removeAllListeners();
    listenersBound = false;
    return true;
  } catch (err) {
    console.error("[native-push] disable failed", err);
    return false;
  }
}

/** Clears the OS notification tray / delivered notifications. */
export async function clearNativeDelivered(): Promise<void> {
  const plugin = await loadPlugin();
  if (!plugin) return;
  try {
    await plugin.removeAllDeliveredNotifications();
  } catch {
    /* non-fatal */
  }
}
